import React from 'react';
import { Document, Page, PDFViewer, Text, View } from '@react-pdf/renderer';

import { pdfStyles } from 'pages/ocr/style';
import prettierStatus, { stopFactorStatusTranscript } from 'utils/prettier-status';
import { useClientContext } from '..';
import ResultBox from '../components/result-box';
import mainTranscript from 'utils/transcript';
import transcript from '../utils/transcript';

const Result = () => {
	const { group, applications, stop_factor_list } = useClientContext();

	if (!group.is_approved || !stop_factor_list || applications.length === 0)
		return (
			<>
				<div />
				<ResultBox />
			</>
		);
	return (
		<>
			<PDFViewer style={{ width: '100%', height: 'calc(100vh - 150px)' }}>
				<Document>
					<Page style={pdfStyles.page}>
						<View style={pdfStyles.heading}>
							<Text>
								Result:{' '}
								<Text
									style={
										(group.expert_decision ?? group.is_approved) === 'approved'
											? pdfStyles.approved
											: (group.expert_decision ?? group.is_approved) === 'rejected'
												? pdfStyles.rejected
												: pdfStyles.review
									}
								>
									{prettierStatus(group.expert_decision ?? group.is_approved)}
								</Text>
							</Text>
						</View>
						<View>
							<Text style={pdfStyles.text}>
								Date/Time: {new Date(group.created_at ?? '').toLocaleString()}
							</Text>
							<Text style={pdfStyles.text}>Group ID: {group.id}</Text>
							<Text style={pdfStyles.text}>
								{group.prediction && `Prediction: ${group.prediction.toFixed(2)}%`}
							</Text>
						</View>
						<View style={pdfStyles.section}>
							<Text style={pdfStyles.title}>Credit info</Text>
							<View style={pdfStyles.table}>
								{row2('Amount', `${group.applications[0].amount.toFixed(2)} JOD`)}
								{row2('Duration', `${group.applications[0].duration} mon.`)}
								{row2(
									'Interest rate (annual)',
									`${(group.applications[0].interest_rate * 100).toFixed(2)} %`,
								)}
								{row2(
									'Threshold',
									`${Number(group.applications[0].scoring_log?.threshold) * 100} %`,
								)}
								{row2(
									'Branch',
									mainTranscript.branch.getValueFromKey(
										group.applications[0].scoring_log?.branch ?? '',
									),
								)}
								{row2(
									'Prohibited loan purpose',
									group.applications[0].scoring_log?.loan_purpose ? 'Yes' : 'No',
								)}
								{row2(
									'Active loans',
									group.applications[0]?.active_loans_credit_cards ? 'Yes' : 'No',
								)}
								{row2(
									'Client residence',
									mainTranscript.city.getValueFromKey(
										group.applications[0].scoring_log?.city ?? '',
									),
								)}
								{row2(
									'Current application with same family ID',
									group.applications[0].scoring_log?.current_application_with_same_family_id
										? 'Yes'
										: 'No',
								)}
								{row2('Repeated client?', String(group.applications[0].repeated_client))}
								{row2(
									'Loan duration of current loan',
									String(group.applications[0].loan_duration_of_current_loan),
								)}
								{row2('Prior loans', String(group.applications[0].prior_loans))}
								{row2('Number of payments', String(group.applications[0].number_of_payments))}
							</View>
						</View>
					</Page>
					{applications.map((app, index) => {
						const stop_factor = stop_factor_list.find((s) => s.id === app.id);
						return (
							<React.Fragment key={app.id}>
								<Page style={pdfStyles.page}>
									<View style={pdfStyles.heading}>
										<Text>
											Client {index + 1}:{' '}
											<Text
												style={
													app?.is_approved === 'approved'
														? pdfStyles.approved
														: app?.is_approved === 'rejected'
															? pdfStyles.rejected
															: pdfStyles.review
												}
											>
												{app?.is_approved &&
													(app?.is_approved === 'approved'
														? 'Approved'
														: app?.is_approved === 'rejected'
															? 'Rejected'
															: 'Review')}
											</Text>
										</Text>
									</View>
									{app.scoring_log?.prediction && (
										<View style={pdfStyles.heading3}>
											<Text>
												{app.scoring_log.prediction && `Prediction: ${app.scoring_log.prediction}%`}
											</Text>
										</View>
									)}
									<View style={pdfStyles.section}>
										<Text style={pdfStyles.title}>Client data</Text>
										<View style={pdfStyles.table}>
											{row2('Gender', transcript.gender(app.scoring_log?.gender ?? ''))}
											{row2(
												'Family status',
												transcript.family_status(app.scoring_log?.family_status ?? ''),
											)}
											{row2('Dependants', app.scoring_log?.dependants)}
											{row2(
												'Client residence',
												mainTranscript.city.getValueFromKey(app.scoring_log?.city ?? ''),
											)}
										</View>
									</View>
									<View style={pdfStyles.section}>
										<Text style={pdfStyles.title}>Alternative data</Text>
										<View style={pdfStyles.table}>
											{row2('Phone OS', app.scoring_log?.phone_os)}
											{row2('Transport', app.scoring_log?.transport)}
											{row2('Residence Type', app.scoring_log?.residence_type)}
											{row2('Employment Level', app.scoring_log?.employment_level)}
											{row2('Employment Status', app.scoring_log?.employment_status)}
											{row2('Work Experience', app.scoring_log?.work_experience)}
											{row2(
												'Transport Manufacturing Year',
												app.scoring_log?.transport_manufacturing_year,
											)}
											{row2('Nationality', app.scoring_log?.nationality)}
											{row2('Address', app.scoring_log?.address)}
											{row2('Postponed payments/installments', app.scoring_log?.postpone_payments)}
											{row2(
												'Project net monthly income',
												`${app.scoring_log?.project_net_monthly_income.toFixed(2)} JOD`,
											)}
											{row2(
												'Additional income',
												`${app.scoring_log?.additional_income.toFixed(2)} JOD`,
											)}
											{row2(
												'Client monthly payment for active loans (non-AMC)',
												`${app.scoring_log?.monthly_payment_for_active_loans.toFixed(2)} JOD`,
											)}
											{row2(
												'Monthly payment',
												`${app.scoring_log?.monthly_payment?.toFixed(2)} JOD`,
											)}
											{row2(
												'Client has early settlement loan',
												app.scoring_log?.client_has_early_settlement_loan,
											)}
										</View>
									</View>
								</Page>
								<Page style={pdfStyles.page}>
									<View style={pdfStyles.heading3}>
										<Text>Client {index + 1}</Text>
									</View>
									<View style={pdfStyles.section}>
										<Text style={pdfStyles.title}>Manual stop factors</Text>
										<View style={pdfStyles.table}>
											{row3(
												'Prohibited loan purpose',
												app.scoring_log?.loan_purpose ? 'Yes' : 'No',
												stopFactorStatusTranscript(stop_factor?.loan_purpose),
											)}
											{row3(
												'Age',
												app.scoring_log?.age + ' y/o',
												stopFactorStatusTranscript(stop_factor?.age),
											)}
											{row3(
												'Dual loans',
												app.scoring_log?.dual_loans ? 'Yes' : 'No',
												stopFactorStatusTranscript(stop_factor?.dual_loans),
											)}
											{row3(
												'Longest payment delay by client (in days)',
												app.scoring_log?.max_days_late_payments,
												stopFactorStatusTranscript(stop_factor?.max_days_late_payments),
											)}
											{row3(
												'Client is already a guarantor for another active loan?',
												app.scoring_log?.client_status_gurantor ? 'Yes' : 'No',
												stopFactorStatusTranscript(stop_factor?.client_status_gurantor),
											)}
											{row3(
												'Family members delinquent (in the same house)',
												app.scoring_log?.family_members_delinquent ? 'Yes' : 'No',
												stopFactorStatusTranscript(stop_factor?.family_members_delinquent),
											)}
											{row3(
												'Active loan with same family ID',
												app.scoring_log?.active_loan_with_same_family_id,
												stopFactorStatusTranscript(stop_factor?.active_loan_with_same_family_id),
											)}
											{row3(
												'Current application with same family ID',
												app.scoring_log?.current_application_with_same_family_id,
												stopFactorStatusTranscript(
													stop_factor?.current_application_with_same_family_id,
												),
											)}
											{row3(
												'Additional income',
												app.scoring_log?.additional_income + ' JOD',
												stopFactorStatusTranscript(stop_factor?.additional_income),
											)}
											{row3(
												'Client DBR',
												(Number(app.scoring_log?.client_dbr) * 100).toFixed(1) + '%',
												stopFactorStatusTranscript(stop_factor?.client_dbr),
											)}
											{row3(
												'Date of early settlement loan',
												app.scoring_log?.date_of_early_settlements_loan,
												stopFactorStatusTranscript(stop_factor?.date_of_early_settlements_loan),
											)}
											{row3(
												'Nationality',
												app.scoring_log?.nationality,
												stopFactorStatusTranscript(stop_factor?.nationality),
											)}
										</View>
									</View>
								</Page>
								<Page style={pdfStyles.page}>
									<View style={pdfStyles.heading3}>
										<Text>Client {index + 1}</Text>
									</View>
									<View style={pdfStyles.section}>
										<Text style={pdfStyles.title}>Legal case</Text>
										<View style={pdfStyles.table}>
											{row3(
												'Risky legal case',
												app.legal_case_log?.legal_case_has_correct_type ?? 'N/A',
												stopFactorStatusTranscript(app.legal_case_log?.is_approved),
											)}
										</View>
									</View>
								</Page>
							</React.Fragment>
						);
					})}
				</Document>
			</PDFViewer>
			<ResultBox />
		</>
	);
};

const row2 = (key: any, value: any) => (
	<View style={pdfStyles.tr}>
		<View style={pdfStyles.td}>
			<Text>{key}</Text>
		</View>
		<View style={pdfStyles.td}>
			<Text>{value}</Text>
		</View>
	</View>
);

const row3 = (col1: any, col2: any, col3: any) => (
	<View style={pdfStyles.tr}>
		<View style={pdfStyles.td}>
			<Text>{col1}</Text>
		</View>
		<View style={pdfStyles.td}>
			<Text>{col2}</Text>
		</View>
		<View style={pdfStyles.td}>
			<Text>{col3}</Text>
		</View>
	</View>
);

export default Result;
